import { differenceInYears, isBefore, setYear } from 'date-fns';

import { compareValues } from '@utils/comparison';

import {
  DeprecatedDisplayCondition,
  DisplayCondition,
  FieldCondition,
  FormExternalConditionData,
  FormStateValues,
} from './types';

export const ssnToDateComponents = (ssn: string) => {
  const lastCentury = ssn.endsWith('9');
  const yearBase = lastCentury ? 1900 : 2000;

  const year = yearBase + Number(ssn.substring(4, 6));
  const month = Number(ssn.substring(2, 4));
  const day = Number(ssn.substring(0, 2));

  return { year, month, day };
};

export const getAgeFromSsn = (ssn: string | null, atEndOfYear = false) => {
  if (!ssn) return null;
  const stripped = ssn.replace(/\D/g, '');
  if (stripped.length !== 10) return null;

  const { year, month, day } = ssnToDateComponents(stripped);
  const today = new Date();
  const ssnToDate = new Date(year, month - 1, day);

  const birthdayCurrentYear = setYear(ssnToDate, today.getFullYear());
  const birthdayBeforeToday = isBefore(birthdayCurrentYear, today);
  const difference = differenceInYears(today, ssnToDate);

  if (atEndOfYear) {
    return birthdayBeforeToday ? difference : difference + 1;
  }

  return difference;
};

/**
 * Check whether a display condition is satisfied for a conditional field.
 */
const checkCondition = (
  condition: FieldCondition,
  externalData: FormExternalConditionData,
  values: FormStateValues,
): boolean => {
  switch (condition.source) {
    case 'external':
      return externalConditionIsValid(condition, externalData);
    case 'form':
      return formConditionIsValid(condition, values);
    default:
      return false;
  }
};

/**
 * Check whether an age condition is satisfied
 */
const checkAgeCondition = (condition: FieldCondition, ssn: string): boolean => {
  const age = getAgeFromSsn(ssn, condition.query === 'ageYearEnd');
  if (!age) return false;
  if (typeof condition.value !== 'number') return false;

  return compareValues(age, condition.comparator, condition.value);
};

/**
 * Checks whether a condition is met using a value retrieved from the external data
 */
const externalConditionIsValid = (
  condition: FieldCondition,
  externalData: FormExternalConditionData,
): boolean => {
  if (condition.query === 'age' || condition.query === 'ageYearEnd') {
    if (!externalData.ssn || typeof externalData.ssn !== 'string') return false;

    return checkAgeCondition(condition, externalData.ssn);
  }

  return false;
};

/**
 * Check whether a condition is met using a value from the form
 */
const formConditionIsValid = (condition: FieldCondition, values: FormStateValues): boolean => {
  const value = values[condition.property];
  if (value === undefined) return false;

  if (condition.query === 'age' || condition.query === 'ageYearEnd') {
    if (!value || typeof value !== 'string') return false;
    return checkAgeCondition(condition, value);
  }

  return compareValues(condition.value, condition.comparator, value as any);
};

/**
 * Check whether a display condition of the old conditional field is satisfied.
 */
const checkOldCondition = (
  data: DeprecatedDisplayCondition,
  externalData: FormExternalConditionData,
): boolean => {
  const { on, query, check, compare } = data;
  if (!on.startsWith('application')) return false;

  const field = on.split('.')[1];
  if (field === 'ssn') {
    if (!externalData.ssn) return false;
    if (typeof externalData?.ssn !== 'string') return false;
    const ssn = externalData.ssn.replace(/\D/g, '');
    switch (query) {
      case 'age':
        return compareValues(getAgeFromSsn(ssn), check, compare);
      default:
        return false;
    }
  }

  return false;
};

/**
 * Check whether a field should be displayed given the current form values
 * and application data.
 */
export const shouldDisplayField = (
  data: DisplayCondition,
  externalData: FormExternalConditionData,
  values: FormStateValues = {},
): boolean => {
  if ('on' in data) {
    // 'on' only exists in the old type, so do the old check
    return checkOldCondition(data, externalData);
  }

  // AND, OR, NOT and IS are all recursive fields, so we check them first
  if (data.AND) {
    return data.AND.every((condition) => shouldDisplayField(condition, externalData, values));
  }

  if (data.OR) {
    return data.OR.some((condition) => shouldDisplayField(condition, externalData, values));
  }

  if (data.NOT) {
    return !shouldDisplayField(data.NOT, externalData, values);
  }

  if (data.IS) {
    return shouldDisplayField(data.IS, externalData, values);
  }

  // If we reach here, data is a basic FieldCondition
  return checkCondition(data, externalData, values);
};

/**
 * Extract property names that should be watched in the state for conditional fields
 */
export const extractWatchingIdsFromCondition = (condition: DisplayCondition): string[] => {
  if ('on' in condition) return [condition.on];

  if ('property' in condition) return [condition.property];

  if ('AND' in condition && condition.AND) {
    return condition.AND.map(extractWatchingIdsFromCondition).flat();
  }

  if ('OR' in condition && condition.OR) {
    return condition.OR.map(extractWatchingIdsFromCondition).flat();
  }

  if ('NOT' in condition && condition.NOT) {
    return extractWatchingIdsFromCondition(condition.NOT);
  }

  return extractWatchingIdsFromCondition(condition.IS);
};
